import { Menu, Transition } from "@headlessui/react";
import clsx from "clsx";
import { Fragment, ReactNode } from "react";

import { ArrowDown } from "@/assets/icons/icons";
import { FCC } from "@/types";

type CustomStyleProps = {
  arrow?: string;
  button?: string;
  buttonText?: string;
  container?: string;
  iconContainer?: string;
  menu?: string;
};

type Props = {
  align?: "center" | "left" | "right";
  customStyles?: CustomStyleProps;
  icon: ReactNode;
  id: string;
  label: string;
  position?: "bottom" | "left" | "right" | "top";
};

export const CollapsibleMenuItem: FCC<Props> = ({
  id,
  align = "left",
  position = "bottom",
  icon,
  label,
  customStyles,
  children,
}) => {
  const getPositionStyles = () => {
    const positions = {
      top: "md:bottom-full md:mb-2",
      bottom: "md:top-full md:mt-2",
      left: "md:right-full md:mr-2 md:top-0",
      right: "md:left-full md:ml-2 md:top-0",
    };

    let alignmentClass = "";
    if (position === "top" || position === "bottom") {
      const alignments = {
        left: "md:left-0",
        center: "md:left-1/2 md:transform md:-translate-x-1/2",
        right: "md:right-0",
      };
      alignmentClass = alignments[align];
    }

    return `${positions[position]} ${alignmentClass}`;
  };

  return (
    <Menu
      as="div"
      data-testid={`collapsible-${id}`}
      className={clsx(
        "relative inline-block text-left",
        customStyles?.container,
      )}
    >
      {({ open }) => (
        <>
          <Menu.Button
            tabIndex={0}
            aria-label={label}
            className={clsx(
              "group flex items-center gap-2 py-2 text-lg text-white hover:font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 md:py-0",
              customStyles?.button,
              open && "!font-semibold",
            )}
          >
            <div
              className={clsx(
                "flex h-10 items-center justify-center border-transparent group-hover:border-white group-hover:bg-transparent md:h-10 md:w-10 md:rounded-full md:border-2",
                customStyles?.iconContainer || "md:bg-white",
              )}
            >
              {icon}
            </div>
            <span
              className={clsx(
                "font-inherit my-auto whitespace-nowrap pb-px text-white md:hidden",
                customStyles?.buttonText,
              )}
            >
              {label}
            </span>
            <ArrowDown
              className={clsx(
                "stroke-white transition ease-linear md:hidden",
                customStyles?.arrow,
                open && "rotate-180",
              )}
            />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              id={`collapsible-menu-${id}`}
              className={clsx(
                "font-small z-20 ml-14 inline-block origin-bottom whitespace-nowrap rounded-md pt-6 text-white shadow-none focus:outline-none md:absolute md:ml-0 md:origin-top-left md:bg-white md:p-4 md:text-primary-100 md:shadow-lg md:ring-1 md:ring-black md:ring-opacity-5",
                customStyles?.menu,
                getPositionStyles(),
              )}
            >
              {children}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};
